import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Eye, FileDown, Send, Inbox, Paperclip, CheckCircle2, Trash2, BadgeCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { money } from "./ClientesModule";
import { ReciboDocumento, descargarReciboPdf } from "./ReciboCobro";

const METODOS_PAGO = [
  { v: "transferencia", label: "Transferencia" },
  { v: "sinpe", label: "SINPE Móvil" },
  { v: "efectivo", label: "Efectivo" },
];

export default function RecibosHistorial() {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(null); // recibo a visualizar
  const [payForm, setPayForm] = useState(null); // recibo al que se le adjunta pago
  const [metodo, setMetodo] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState("");
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/receipts");
      setReceipts((data || []).filter((r) => r.tipo === "manual"));
    } catch {
      toast.error("No se pudo cargar el historial de recibos.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const download = async () => {
    setDownloading(true);
    try {
      await descargarReciboPdf("recibo-hist-manual", view.numero);
    } finally {
      setDownloading(false);
    }
  };

  const openPay = (r) => {
    setPayForm(r);
    setMetodo("");
    setFile(null);
  };

  const submitPay = async () => {
    if (!metodo) { toast.error("Elija el método de pago."); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("metodo_pago", metodo);
      if (file) fd.append("comprobante", file);
      const { data } = await api.post(`/admin/receipts/${payForm.id}/cobrar`, fd);
      setReceipts((prev) => prev.map((x) => (x.id === payForm.id ? { ...x, cobro_id: data.id, metodo_pago: data.metodo_pago, fecha_pago: data.fecha_cobro } : x)));
      toast.success(`Pago registrado en Registro de Cobros: ${money(data.monto, data.currency)} · ${payForm.numero}`);
      setPayForm(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo registrar el pago.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    if (confirmDelete !== r.id) {
      setConfirmDelete(r.id);
      setTimeout(() => setConfirmDelete((c) => (c === r.id ? "" : c)), 4000);
      return;
    }
    setDeleting(true);
    try {
      await api.delete(`/admin/receipts/${r.id}`);
      setReceipts((prev) => prev.filter((x) => x.id !== r.id));
      if (view?.id === r.id) setView(null);
      toast.success(`Recibo ${r.numero} eliminado del historial.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo eliminar el recibo.");
    } finally {
      setDeleting(false);
      setConfirmDelete("");
    }
  };

  return (
    <div data-testid="receipts-history">
      {loading ? (
        <p className="text-gray-500 py-12 text-center">Cargando recibos...</p>
      ) : receipts.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <Inbox className="h-10 w-10 mx-auto mb-3 opacity-40" />
          Aún no hay recibos manuales. Cree uno desde la pestaña "Recibo Manual".
        </div>
      ) : (
        <div className="space-y-3" data-testid="receipts-list">
          {receipts.map((r) => (
            <div key={r.id} className="rounded-xl bg-vnavy-800 border border-white/10 px-5 py-4 flex items-center gap-3 flex-wrap" data-testid={`receipt-row-${r.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-white font-medium">{r.company}</p>
                  {r.recurrente && (
                    <Badge className="border bg-vteal-light/15 text-vteal-light border-vteal-light/30" data-testid={`receipt-frecuente-${r.id}`}>
                      Frecuente · {r.frecuencia === "quincenal" ? "Quincenal" : "Mensual"}
                    </Badge>
                  )}
                  {r.cobro_id ? (
                    <Badge className="border bg-green-500/15 text-green-300 border-green-500/30" data-testid={`receipt-pagado-${r.id}`}>
                      Pagado · {(METODOS_PAGO.find((m) => m.v === r.metodo_pago) || {}).label || r.metodo_pago}
                    </Badge>
                  ) : (
                    <Badge className="border bg-amber-500/15 text-amber-300 border-amber-500/30" data-testid={`receipt-pendiente-${r.id}`}>Pendiente de pago</Badge>
                  )}
                </div>
                <p className="text-gray-500 text-xs mt-1">N° {r.numero} · Cobro: {r.fecha}</p>
              </div>
              <p className="text-white font-semibold shrink-0">{money(r.total, r.currency)}</p>
              <div className="flex items-center gap-2 shrink-0 flex-wrap">
                <button onClick={() => setView(r)} data-testid={`receipt-view-${r.id}`}
                  className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-white/15 text-gray-300 hover:border-vteal-light hover:text-white transition-colors">
                  <Eye className="h-3.5 w-3.5" /> Ver
                </button>
                {!r.cobro_id && (
                  <button onClick={() => openPay(r)} data-testid={`receipt-pay-${r.id}`}
                    className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-green-500/40 text-green-300 hover:bg-green-500/10 transition-colors">
                    <BadgeCheck className="h-3.5 w-3.5" /> Adjuntar pago
                  </button>
                )}
                <button onClick={() => remove(r)} disabled={deleting} data-testid={`receipt-delete-${r.id}`} title="Eliminar del historial"
                  className={`flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border transition-colors disabled:opacity-60 ${confirmDelete === r.id ? "border-red-500 bg-red-500/20 text-red-200 font-semibold" : "border-white/15 text-gray-400 hover:border-red-500/50 hover:text-red-300"}`}>
                  <Trash2 className="h-3.5 w-3.5" /> {confirmDelete === r.id ? "¿Confirmar?" : ""}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Ver recibo */}
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="bg-vnavy-800 border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="receipt-view-modal">
          <DialogHeader>
            <DialogTitle className="font-serif">Recibo {view?.numero}</DialogTitle>
            <DialogDescription className="text-gray-400">{view?.company} · Enviado el {view?.emitido}</DialogDescription>
          </DialogHeader>
          {view && (
            <div className="space-y-4">
              <div className="overflow-x-auto rounded-lg">
                <ReciboDocumento d={{ ...view, fecha_display: view.fecha }} id="recibo-hist-manual" />
              </div>
              <div className="flex gap-2 justify-end">
                <button onClick={download} disabled={downloading} data-testid="receipt-download"
                  className="flex items-center gap-1.5 text-xs px-4 py-2 rounded-lg border border-white/15 text-gray-300 hover:border-vteal-light hover:text-white transition-colors disabled:opacity-60">
                  <FileDown className="h-3.5 w-3.5" /> {downloading ? "Generando..." : "Descargar PDF"}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Adjuntar pago */}
      <Dialog open={!!payForm} onOpenChange={(o) => !o && setPayForm(null)}>
        <DialogContent className="bg-vnavy-800 border-white/10 text-white max-w-md" data-testid="receipt-pay-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif">Adjuntar pago</DialogTitle>
            <DialogDescription className="text-gray-400">
              {payForm?.numero} · {payForm?.company} · <span className="text-vteal-light font-semibold">{money(payForm?.total, payForm?.currency)}</span>
              <br />Se registrará en Registro de Cobros con la fecha de hoy.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-1.5">Método de pago *</p>
              <div className="flex rounded-lg overflow-hidden border border-white/10">
                {METODOS_PAGO.map((m) => (
                  <button key={m.v} type="button" onClick={() => setMetodo(m.v)} data-testid={`receipt-pay-metodo-${m.v}`}
                    className={`flex-1 text-xs py-2.5 px-1 font-semibold transition-colors ${metodo === m.v ? "bg-vteal-light text-vnavy" : "text-gray-400 hover:text-white"}`}>
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-1.5">Comprobante de pago (PDF o imagen, opcional)</p>
              <label className="flex items-center gap-2 rounded-lg border border-dashed border-white/20 px-3 py-3 cursor-pointer hover:border-vteal-light/60 transition-colors">
                <Paperclip className="h-4 w-4 text-vteal-light shrink-0" />
                <span className="text-sm text-gray-300 truncate">{file ? file.name : "Adjuntar comprobante del cliente..."}</span>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" data-testid="receipt-pay-comprobante"
                  onChange={(e) => setFile(e.target.files?.[0] || null)} />
              </label>
            </div>
            <button onClick={submitPay} disabled={saving} data-testid="receipt-pay-submit"
              className="w-full flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-vteal-light text-vnavy text-sm font-semibold hover:bg-white transition-colors disabled:opacity-60">
              <CheckCircle2 className="h-4 w-4" /> {saving ? "Registrando..." : "Registrar pago"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
